import useSWR, { useSWRConfig } from 'swr';

import { fetchData, fetchJson } from '~/lib/fetch';
import {
  FacebookLoginResponseProps,
  UserHookProps,
  UserLoginProps,
} from '~/types/user';
import {
  SIGNIN_ENDPOINT_URL,
  USER_SESSION_ENDPOINT_URL,
  SIGNUP_ENDPOINT_URL,
  GOOGLE_REGISTER_ENDPOINT_URL,
  FACEBOOK_REGISTER_ENDPOINT_URL,
} from '~/lib/login/constants';
import { ACCOUNT_ERRORS } from '~/lib/checkout/constants';
import { CredentialResponse } from '@react-oauth/google';

const DEFAULT_POST_ARGS = {
  method: 'POST',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

export default function useUser(): UserHookProps {
  const { mutate, data, error } = useSWR(USER_SESSION_ENDPOINT_URL);
  const { mutate: asyncMutate } = useSWRConfig();
  const { user, session, messages, cart } = data || {};

  const sessionHelper = async (): Promise<UserLoginProps> => {
    const session = await asyncMutate(USER_SESSION_ENDPOINT_URL);
    const error = !session?.user?.id ? ACCOUNT_ERRORS.FETCH_USER_SESSION : null;

    return { session, error };
  };

  // Using a callback here because we need to get the session data after logging in
  // Facebook will give an error if the outer function is async
  const facebookSignIn = (
    response: FacebookLoginResponseProps,
    callback: (asyncSessionData: () => Promise<UserLoginProps>) => void,
  ): void => {
    const signInError = ACCOUNT_ERRORS.FACEBOOK_LOGIN;

    if (response.authResponse) {
      const { accessToken } = response.authResponse;

      const args = {
        ...DEFAULT_POST_ARGS,
        body: JSON.stringify({ access_token: accessToken }),
      };

      callback(async () => {
        try {
          // Send the access token to the server
          await fetchData(FACEBOOK_REGISTER_ENDPOINT_URL, args);
          return sessionHelper();
        } catch (facebookError) {
          return { session: null, error: signInError };
        }
      });
    } else {
      callback(() => Promise.resolve({ session: null, error: signInError }));
    }
  };

  const googleSignIn = async (
    response: CredentialResponse,
  ): Promise<UserLoginProps> => {
    const signInError = ACCOUNT_ERRORS.GOOGLE_LOGIN;

    if (response.credential) {
      const accessToken = response.credential;
      const args = {
        ...DEFAULT_POST_ARGS,
        body: JSON.stringify({ access_token: accessToken }),
      };

      try {
        await fetchJson(GOOGLE_REGISTER_ENDPOINT_URL, args);

        return sessionHelper();
      } catch (googleError) {
        // TODO -- find out what this error could be
      }
    }

    return { session: null, error: signInError };
  };

  const signUp = async (
    email: string,
    password: string,
    confirmation: string,
    token: string,
  ): Promise<UserLoginProps> => {
    const args = {
      ...DEFAULT_POST_ARGS,
      body: JSON.stringify({
        email: email,
        password1: password,
        password2: confirmation,
        token: token,
      }),
    };

    // Create the user (and update the session)
    // Use useSWRConfig mutate (instead of useSWR) so we can return the session and check for errors
    try {
      await fetchData(SIGNUP_ENDPOINT_URL, args);
      return sessionHelper();
    } catch (e: any) {
      let signUpError = ACCOUNT_ERRORS.GENERIC_SIGNUP;

      if (e.response.status === 400) {
        try {
          const { password1, email, non_field_errors } =
            await e.response.json();

          if (email) {
            signUpError = email[0] || ACCOUNT_ERRORS.EMAIL;
          } else if (password1) {
            signUpError = password1[0] || ACCOUNT_ERRORS.PASSWORD;
          } else if (non_field_errors) {
            signUpError = non_field_errors[0] || error;
          }
        } catch (jsonError) {
          // Tried to parse json...
        }
      }

      return { session: null, error: signUpError };
    }
  };

  const signIn = async (
    email: string,
    password: string,
    token: string,
  ): Promise<UserLoginProps> => {
    const args = {
      ...DEFAULT_POST_ARGS,
      body: JSON.stringify({
        email: email,
        password: password,
        token: token,
      }),
    };

    try {
      // Login (and update the session)
      // Use useSWRConfig mutate (instead of useSWR) so we can return the session and check for errors
      await fetchData(SIGNIN_ENDPOINT_URL, args);
      return sessionHelper();
    } catch (e: any) {
      let signInError = ACCOUNT_ERRORS.GENERIC_LOGIN;

      if (e.response.status === 400) {
        try {
          const { non_field_errors } = await e.response.json();

          if (non_field_errors) {
            signInError = non_field_errors[0] || error;
          }
        } catch (jsonError) {
          // Tried to parse json...
        }
      }

      return { session: null, error: signInError };
    }
  };

  return {
    mutate,
    user,
    messages,
    session,
    cart,
    isLoading: !error && !data,
    isError: error,
    signUp,
    signIn,
    googleSignIn,
    facebookSignIn,
  };
}
